/**
 * Catálogo de basemaps do mapa (M2) — GUPM-194. TS puro (config, sem React/DOM).
 * Todos keyless com atribuição. Bing fora (fere os termos da Microsoft — usava
 * quadkey sem chave). Fonte da disposição: protótipo V8 (`map-type-pills`).
 *
 * `hybrid` = satélite (ArcGIS) + overlay de rótulos (CartoDB), renderizado por
 * cima — fiel ao "Híbrido" do V8.
 */
export type BasemapMode = 'satellite' | 'topo' | 'osm' | 'dark' | 'hybrid';

/**
 * Zoom para onde o mapa voa ao selecionar um instrumento, e teto do zoom manual.
 *
 * GUPM-229: os dois passaram a ser constantes porque precisam ser comparáveis com
 * o `maxZoom` de cada basemap. Antes o `flyTo` ia a 18 com um basemap declarando
 * `maxZoom: 17` — a camada sumia inteira ao trocar para ele.
 */
export const FLY_TO_ZOOM = 18;
export const MAP_MAX_ZOOM = 20;

export interface TileDef {
  url: string;
  attribution: string;
  subdomains?: string[];
  /**
   * Teto de zoom da camada. Entre `maxNativeZoom` e este valor o Leaflet
   * REESCALA o último tile disponível (borrado, mas visível).
   */
  maxZoom: number;
  /**
   * Último zoom em que o provedor tem imagem própria. Sem isto o Leaflet para de
   * desenhar acima do zoom nativo em vez de reescalar — é o "fundo desaparece em
   * zoom alto" do GUPM-229.
   */
  maxNativeZoom: number;
}

export interface Basemap {
  id: BasemapMode;
  label: string;
  base: TileDef;
  /** Camada renderizada POR CIMA da base (ex.: rótulos do híbrido). */
  overlay?: TileDef;
}

const ARCGIS_SAT: TileDef = {
  url: 'https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  attribution: 'Imagery &copy; Esri, Maxar, Earthstar Geographics',
  // O World_Imagery declara 19 global, mas a cobertura em zona rural do Alto
  // Paranaíba costuma parar antes. 18 é o nativo conservador: acima disso
  // reescala em vez de arriscar tile vazio. Se a imagem de 19 existir para a
  // UPM, é uma linha para subir.
  maxNativeZoom: 18,
  maxZoom: MAP_MAX_ZOOM,
};

/** Só rótulos (ruas/cidades) transparentes — overlay do híbrido, por cima do satélite. */
const CARTO_LABELS: TileDef = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png',
  subdomains: ['a', 'b', 'c', 'd'],
  attribution: '&copy; OpenStreetMap, &copy; CARTO',
  maxNativeZoom: 19,
  maxZoom: MAP_MAX_ZOOM,
};

export const BASEMAPS: Basemap[] = [
  { id: 'satellite', label: '🛰 Satélite', base: ARCGIS_SAT },
  { id: 'hybrid', label: '🛰 Híbrido', base: ARCGIS_SAT, overlay: CARTO_LABELS },
  {
    id: 'topo',
    label: '⛰ Topo',
    // O OpenTopoMap só publica até 17 — era o basemap que apagava o fundo ao
    // voltar de um pino selecionado (flyTo 18). Agora reescala.
    base: { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', subdomains: ['a', 'b', 'c'], attribution: '&copy; OpenTopoMap (CC-BY-SA)', maxNativeZoom: 17, maxZoom: MAP_MAX_ZOOM },
  },
  {
    id: 'osm',
    label: '☀ Claro',
    base: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', subdomains: ['a', 'b', 'c'], attribution: '&copy; OpenStreetMap', maxNativeZoom: 19, maxZoom: MAP_MAX_ZOOM },
  },
  {
    id: 'dark',
    label: '🌙 Escuro',
    base: { url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', subdomains: ['a', 'b', 'c', 'd'], attribution: '&copy; OpenStreetMap, &copy; CartoDB', maxNativeZoom: 19, maxZoom: MAP_MAX_ZOOM },
  },
  // ⚠️ Fase red (GUPM-194): `hybrid` será adicionado na fase green.
];

export function getBasemap(mode: BasemapMode): Basemap {
  return BASEMAPS.find((b) => b.id === mode) ?? BASEMAPS[0]!;
}
